import { Component, OnInit, inject } from "@angular/core"
import { CommonModule } from "@angular/common"
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from "@angular/forms"
import { RouterModule } from "@angular/router"
import { OwnerService, Owner, OwnersResponse } from "../../services/owner.service"
import { UploadService } from "../../services/upload.service"
import { firstValueFrom } from "rxjs"
import {
  CommonTableComponent,
  TableColumn,
  TableAction,
} from "../../shared/common-table/common-table.component"
import { PageHeaderComponent } from "../../shared/page-header/page-header.component"
import { SnackbarService } from "../../services/snackbar.service"
import { ConfirmDialogService } from "../../services/confirm-dialog.service"

@Component({
  selector: "app-manage-owners",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    CommonTableComponent,
    PageHeaderComponent,
  ],
  templateUrl: "./manage-owners.component.html",
  styleUrls: ["./manage-owners.component.css"],
})
export class ManageOwnersComponent implements OnInit {
  private ownerService = inject(OwnerService)
  private uploadService = inject(UploadService)
  private formBuilder = inject(FormBuilder)
  private snackbar = inject(SnackbarService)
  private confirmDialog = inject(ConfirmDialogService)

  owners: Owner[] = []
  loading = false
  isSubmitting = false
  isLoadingOwner = false

  tableColumns: TableColumn[] = [
    { key: "id", label: "ID", width: "80px", class: "th-id" },
    { key: "name", label: "Owner Name", class: "th-name" },
    { key: "station_call_signs", label: "Stations", class: "th-stations" },
    { key: "created_at", label: "Created", class: "th-date" },
  ]

  tableActions: TableAction[] = [
    {
      icon: "edit",
      label: "Edit owner",
      class: "btn-edit",
      callback: (owner: Owner) => this.openEditModal(owner),
    },
    {
      icon: "delete",
      label: "Delete owner",
      class: "btn-delete",
      callback: (owner: Owner) => this.deleteOwner(owner),
    },
  ]

  // Modal state
  isModalOpen = false
  isEditMode = false
  selectedOwner: Owner | null = null
  ownerForm: FormGroup

  // File handling properties
  selectedLogoFile: File | null = null
  selectedInvertedLogoFile: File | null = null

  // Upload state
  logoTmpKey: string | null = null
  invertedLogoTmpKey: string | null = null
  isUploadingLogo = false
  isUploadingInvertedLogo = false

  /** Local FileReader previews + existing URLs in edit mode */
  logoPreviewUrl: string | null = null
  invertedLogoPreviewUrl: string | null = null
  existingLogoUrl: string | null = null
  existingInvertedLogoUrl: string | null = null
  dragOverField: "logo" | "inverted_logo" | null = null

  // Pagination
  currentPage = 1
  itemsPerPage = 10
  totalItems = 0
  totalPages = 0

  /** Max length for optional owner order terms (multi-paragraph content). */
  readonly orderTermsMaxLength = 10000

  constructor() {
    this.ownerForm = this.formBuilder.group({
      name: ["", [Validators.required, Validators.maxLength(100)]],
      firstname: ["", [Validators.required]],
      lastname: ["", [Validators.required]],
      email: ["", [Validators.required, Validators.email]],
      order_terms: ["", [Validators.maxLength(this.orderTermsMaxLength)]],
    })
  }

  ngOnInit(): void {
    this.loadOwners()
  }

  loadOwners(): void {
    this.loading = true
    const offset = (this.currentPage - 1) * this.itemsPerPage

    this.ownerService.getOwners(this.itemsPerPage, offset).subscribe({
      next: (response: OwnersResponse) => {
        this.owners = response.owners || []
        this.totalItems = response.pagination?.total ?? 0
        this.totalPages =
          this.totalItems === 0 ? 0 : Math.ceil(this.totalItems / this.itemsPerPage)
        if (this.totalPages > 0 && this.currentPage > this.totalPages) {
          this.currentPage = this.totalPages
          this.loadOwners()
          return
        }
        this.loading = false
      },
      error: (error) => {
        console.error("Error loading owners:", error)
        const msg = this.snackbar.apiError(error, "Failed to load owners. Please try again.")
        this.snackbar.error(msg)
        this.loading = false
      },
    })
  }

  openAddModal(): void {
    this.isEditMode = false
    this.selectedOwner = null
    this.ownerForm.reset()
    this.resetUploadState()
    this.isModalOpen = true
  }

  openEditModal(owner: Owner): void {
    const listUser = owner.user_details?.length ? owner.user_details[0] : null

    this.isEditMode = true
    this.selectedOwner = owner
    this.isModalOpen = true
    this.isLoadingOwner = true

    this.resetUploadState()

    this.existingLogoUrl = owner.logo_url ?? null
    this.existingInvertedLogoUrl = owner.inverted_logo_url ?? null

    // Show list data immediately so the dialog never flash-empties while loading.
    this.ownerForm.patchValue({
      name: owner.name,
      firstname: listUser?.firstname || "",
      lastname: listUser?.lastname || "",
      email: listUser?.email || "",
      order_terms: owner.order_terms || "",
    })


    // Refresh from GET /owners/{id} for latest order_terms and any other fields.
    this.ownerService.getOwner(owner.id).subscribe({
      next: (fullOwner) => {
        const merged: Owner = {
          ...owner,
          ...fullOwner,
          // Prefer API user_details when present; otherwise keep list row details.
          user_details:
            fullOwner.user_details && fullOwner.user_details.length > 0
              ? fullOwner.user_details
              : owner.user_details,
        }
        this.selectedOwner = merged
        const user = merged.user_details?.length ? merged.user_details[0] : listUser
        this.ownerForm.patchValue({
          name: merged.name || owner.name,
          firstname: user?.firstname || listUser?.firstname || "",
          lastname: user?.lastname || listUser?.lastname || "",
          email: user?.email || listUser?.email || "",
          // API null/empty = no terms; missing field = keep list value.
          order_terms:
            fullOwner.order_terms !== undefined
              ? fullOwner.order_terms || ""
              : owner.order_terms || "",
        })
        this.isLoadingOwner = false
      },
      error: (error) => {
        console.error("Error loading owner for edit:", error)
        this.isLoadingOwner = false
        const msg = this.snackbar.apiError(error, "Failed to refresh owner details. Showing list data.")
        this.snackbar.warning(msg)
      },
    })
  }

  closeModal(): void {
    this.isModalOpen = false
    this.isEditMode = false
    this.selectedOwner = null
    this.isLoadingOwner = false
    this.ownerForm.reset()
    this.resetUploadState()
  }

  onLogoFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement
    if (input.files && input.files.length > 0) {
      const file = input.files[0]
      // Validate file type
      const validTypes = [
        'image/png',
        'image/jpeg',
        'image/jpg',
        'image/webp',
        'image/gif',
        'image/svg+xml',
        'image/bmp',
        'image/tiff',
      ];
      if (!validTypes.includes(file.type.toLowerCase())) {
        this.snackbar.warning("Logo must be PNG, JPG, or WebP format.")
        input.value = ''
        return
      }
      this.selectedLogoFile = file
      this.readImagePreview(file, (url) => (this.logoPreviewUrl = url))

      // Immediate upload
      this.isUploadingLogo = true
      this.uploadService.uploadFile(file, "owners").subscribe({
        next: (res) => {
          this.logoTmpKey = res.tmp_key
          this.isUploadingLogo = false
        },
        error: (err) => {
          console.error("Logo upload failed", err)
          this.isUploadingLogo = false
          this.snackbar.error("Failed to upload logo. Please try again.")
        }
      })
    }
  }

  onInvertedLogoFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement
    if (input.files && input.files.length > 0) {
      const file = input.files[0]
      // Validate file type
      const validTypes = [
        'image/png',
        'image/jpeg',
        'image/jpg',
        'image/webp',
        'image/gif',
        'image/svg+xml',
        'image/bmp',
        'image/tiff',
      ];
      if (!validTypes.includes(file.type.toLowerCase())) {
        this.snackbar.warning("Inverted logo must be PNG, JPG, or WebP format.")
        input.value = ''
        return
      }
      this.selectedInvertedLogoFile = file
      this.readImagePreview(file, (url) => (this.invertedLogoPreviewUrl = url))

      // Immediate upload
      this.isUploadingInvertedLogo = true
      this.uploadService.uploadFile(file, "owners").subscribe({
        next: (res) => {
          this.invertedLogoTmpKey = res.tmp_key
          this.isUploadingInvertedLogo = false
        },
        error: (err) => {
          console.error("Inverted logo upload failed", err)
          this.isUploadingInvertedLogo = false
          this.snackbar.error("Failed to upload inverted logo. Please try again.")
        }
      })
    }
  }

  onDragOver(event: DragEvent, field: "logo" | "inverted_logo"): void {
    event.preventDefault()
    this.dragOverField = field
  }

  onDragLeave(field: "logo" | "inverted_logo"): void {
    if (this.dragOverField === field) this.dragOverField = null
  }

  onLogoDrop(event: DragEvent, input: HTMLInputElement): void {
    this.handleImageDrop(event, input)
  }

  onInvertedLogoDrop(event: DragEvent, input: HTMLInputElement): void {
    this.handleImageDrop(event, input)
  }

  clearLogoPreview(input?: HTMLInputElement): void {
    this.logoPreviewUrl = null
    this.existingLogoUrl = null
    this.selectedLogoFile = null
    this.logoTmpKey = null
    if (input) input.value = ""
  }

  clearInvertedLogoPreview(input?: HTMLInputElement): void {
    this.invertedLogoPreviewUrl = null
    this.existingInvertedLogoUrl = null
    this.selectedInvertedLogoFile = null
    this.invertedLogoTmpKey = null
    if (input) input.value = ""
  }

  onPreviewImageError(field: "logo" | "inverted_logo"): void {
    if (field === "logo" && !this.logoPreviewUrl) this.existingLogoUrl = null
    if (field === "inverted_logo" && !this.invertedLogoPreviewUrl) {
      this.existingInvertedLogoUrl = null
    }
  }

  private handleImageDrop(event: DragEvent, input: HTMLInputElement): void {
    event.preventDefault()
    this.dragOverField = null
    const file = event.dataTransfer?.files?.[0]
    if (!file || !file.type.startsWith("image/")) return
    const transfer = new DataTransfer()
    transfer.items.add(file)
    input.files = transfer.files
    input.dispatchEvent(new Event("change"))
  }

  private readImagePreview(file: File, apply: (dataUrl: string) => void): void {
    const reader = new FileReader()
    reader.onload = (e) => apply((e.target?.result as string) ?? "")
    reader.readAsDataURL(file)
  }

  private resetUploadState(): void {
    this.selectedLogoFile = null
    this.selectedInvertedLogoFile = null
    this.logoTmpKey = null
    this.invertedLogoTmpKey = null
    this.isUploadingLogo = false
    this.isUploadingInvertedLogo = false
    this.logoPreviewUrl = null
    this.invertedLogoPreviewUrl = null
    this.existingLogoUrl = null
    this.existingInvertedLogoUrl = null
    this.dragOverField = null
  }

  async onSubmit(): Promise<void> {
    if (!this.ownerForm.valid) {
      return
    }

    this.isSubmitting = true

    try {
      // Check if uploads are still in progress
      if (this.isUploadingLogo || this.isUploadingInvertedLogo) {
        this.snackbar.info("Please wait for file uploads to complete.")
        this.isSubmitting = false
        return
      }

      // Use pre-uploaded keys
      const logoTmpKey = this.logoTmpKey || undefined
      const invertedLogoTmpKey = this.invertedLogoTmpKey || undefined

      if (this.isEditMode && this.selectedOwner) {
        // Update existing owner
        const updateData = this.ownerService.prepareUpdateOwnerData(
          this.ownerForm.value,
          logoTmpKey,
          invertedLogoTmpKey,
        )

        this.ownerService.updateOwner(this.selectedOwner.id, updateData).subscribe({
          next: () => {
            this.isSubmitting = false
            this.closeModal()
            this.snackbar.success("Owner updated successfully.")
            this.ownerService.clearAllOwnersCache()
            this.loadOwners()
          },
          error: (error) => {
            console.error("Error updating owner:", error)
            const msg = this.snackbar.apiError(error, "Failed to update owner. Please try again.")
            this.snackbar.error(msg)
            this.isSubmitting = false
          },
        })
      } else {
        // Create new owner
        const createData = this.ownerService.prepareOwnerData(
          this.ownerForm.value,
          logoTmpKey,
          invertedLogoTmpKey,
        )

        this.ownerService.createOwner(createData).subscribe({
          next: () => {
            this.isSubmitting = false
            this.closeModal()
            this.snackbar.success("Owner created successfully.")
            this.ownerService.clearAllOwnersCache()
            this.loadOwners()
          },
          error: (error) => {
            console.error("Error creating owner:", error)
            const msg = this.snackbar.apiError(error, "Failed to create owner. Please try again.")
            this.snackbar.error(msg)
            this.isSubmitting = false
          },
        })
      }
    } catch (error) {
      console.error("Error preparing owner data:", error)
      this.snackbar.error("Failed to process owner data. Please try again.")
      this.isSubmitting = false
    }
  }

  deleteOwner(owner: Owner): void {
    this.confirmDialog
      .open({
        title: "Delete owner",
        message: `Are you sure you want to delete "${owner.name}"? This action cannot be undone.`,
        confirmLabel: "Delete",
        cancelLabel: "Cancel",
      })
      .subscribe((confirmed) => {
        if (!confirmed) return
        this.ownerService.deleteOwner(owner.id).subscribe({
          next: () => {
            this.snackbar.success(`Owner "${owner.name}" was deleted.`)
            this.ownerService.clearAllOwnersCache()
            this.loadOwners()
          },
          error: (error) => {
            console.error("Error deleting owner:", error)
            const msg = this.snackbar.apiError(error, "Failed to delete owner. Please try again.")
            this.snackbar.error(msg)
          },
        })
      })
  }

  goToPage(page: number): void {
    if (this.totalPages === 0) return
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page
      this.loadOwners()
    }
  }

  get paginationStart(): number {
    if (this.totalItems === 0) return 0
    return (this.currentPage - 1) * this.itemsPerPage + 1
  }

  get paginationEnd(): number {
    return Math.min(this.currentPage * this.itemsPerPage, this.totalItems)
  }

  getPaginationPages(): number[] {
    const pages: number[] = []
    const maxVisible = 5

    if (this.totalPages <= 0) {
      return pages
    }

    if (this.totalPages <= maxVisible) {
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i)
      }
    } else {
      const start = Math.max(1, this.currentPage - 2)
      const end = Math.min(this.totalPages, start + maxVisible - 1)

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
    }

    return pages
  }

  ownerInitial(owner: Owner): string {
    const name = (owner.name || "").trim()
    if (!name) return "?"
    const parts = name.split(/\s+/).filter(Boolean)
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase()
    }
    return name.slice(0, 2).toUpperCase()
  }

  formatDate(dateString: string): string {
    if (!dateString) return "-"
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  trackByOwnerId(index: number, owner: Owner): number {
    return owner.id
  }

  trackByPage(index: number, page: number): number {
    return page
  }
}
